import { useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, PenSquare, Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import Sidebar from "./Sidebar";
import CommandPalette, { isTypingTarget } from "./CommandPalette";
import DealDrawer from "./deal/DealDrawer";
import { Auth } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { useUIStore } from "@/store/ui";
import { useChatStore } from "@/store/chat";

const NAV_SHORTCUTS: Record<string, string> = {
  h: "/",
  t: "/today",
  d: "/deals",
  k: "/contracts",
  a: "/accounts",
  c: "/contacts",
  l: "/library",
  m: "/demos",
  e: "/activity",
  ",": "/settings",
};

// Admin-only single-key shortcuts, merged in only for admins.
const ADMIN_NAV_SHORTCUTS: Record<string, string> = {
  r: "/dashboard",
};

export default function Layout() {
  const { token, setUser, user } = useAuthStore();
  const navigate = useNavigate();
  const cmdkOpen = useUIStore((s) => s.cmdkOpen);
  const dealOpen = useUIStore((s) => s.openDealId !== null);
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);

  // Any navigation closes the phone menu drawer.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search, setMobileNavOpen]);

  // Refresh `me` so the sidebar shows live user info. Key by token so switching
  // accounts fetches the new user instead of returning a stale cached `me` from
  // the previous session (which would clobber the just-logged-in user below).
  const { data: me } = useQuery({
    queryKey: ["me", token],
    queryFn: () => Auth.me(),
    enabled: !!token,
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (me) setUser(me);
  }, [me, setUser]);

  // Global single-key navigation — bail if the user is typing, if a modifier is
  // held, or if an overlay (palette / deal panel) is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (cmdkOpen || dealOpen) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (e.key.toLowerCase() === "n") {
        // N: a fresh chat, wherever you are.
        e.preventDefault();
        useChatStore.getState().setLastChatId(null);
        navigate("/");
        return;
      }
      const shortcuts = isAdmin ? { ...NAV_SHORTCUTS, ...ADMIN_NAV_SHORTCUTS } : NAV_SHORTCUTS;
      const dest = shortcuts[e.key.toLowerCase()];
      if (dest) {
        e.preventDefault();
        navigate(dest);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cmdkOpen, dealOpen, navigate, isAdmin]);

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0">
        <MobileBar />
        <Outlet />
      </main>
      <CommandPalette />
      <DealDrawer />
    </div>
  );
}

/** Phone-only app bar: menu, brand, search and a new chat. Hidden from `md` up,
 *  where the sidebar is always on screen. */
function MobileBar() {
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);
  const openCmdK = useUIStore((s) => s.openCmdK);
  const setLastChatId = useChatStore((s) => s.setLastChatId);
  return (
    <header className="md:hidden sticky top-0 z-40 flex h-12 items-center gap-1 border-b border-fg/[0.06] bg-ink-950/85 px-2 backdrop-blur-md">
      <button
        className="btn btn-ghost !p-2"
        aria-label="Open menu"
        onClick={() => setMobileNavOpen(true)}
      >
        <Menu className="h-5 w-5" />
      </button>
      <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
        <img src="/favicon.svg" alt="" width={22} height={22} className="rounded-md" />
        LeadZeppelin
      </Link>
      <div className="flex-1" />
      <button className="btn btn-ghost !p-2" aria-label="Search" onClick={openCmdK}>
        <Search className="h-5 w-5" />
      </button>
      <Link
        to="/"
        className="btn btn-ghost !p-2"
        aria-label="New chat"
        onClick={() => setLastChatId(null)}
      >
        <PenSquare className="h-5 w-5" />
      </Link>
    </header>
  );
}
